import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import NotifyProvider from './components/NotifyProvider';
import AppErrorBoundary from './components/AppErrorBoundary';
import { LocaleProvider } from './i18n/LocaleContext';

// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ApproveByToken from './pages/auth/ApproveByToken';

// App pages
import AppLayout from './layouts/AppLayout';
import Inbox from './pages/app/Inbox';
import Leads from './pages/app/Leads';
import Settings from './pages/app/Settings';
import Reports from './pages/app/Reports';
import Approvals from './pages/app/Approvals';
import Posts from './pages/app/Posts';
import Bookings from './pages/app/Bookings';
import Audit from './pages/app/Audit';
import Tasks from './pages/app/Tasks';
import Engagement from './pages/app/engagement/EngagementWorkflow';

// Admin pages
import Admin from './pages/admin/Admin';
import AdminPosts from './pages/admin/AdminPosts';

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F8F6]">
      <div className="w-9 h-9 border-2 border-[#D7DCE4] border-t-[#12C8C6] rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && !isAdmin) return <Navigate to="/app" replace />;
  return children;
}

function AuthOnlyRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to={isAdmin ? '/admin' : '/app'} replace />;
  return children;
}

function RootRoute() {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  return <Navigate to={user ? (isAdmin ? '/admin' : '/app') : '/login'} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />

      {/* Public token-based post approval (linked from emails) */}
      <Route path="/approve/:token" element={<ApproveByToken />} />

      {/* Auth pages (redirect in if logged in) */}
      <Route
        path="/login"
        element={
          <AuthOnlyRoute>
            <Login />
          </AuthOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <AuthOnlyRoute>
            <Register />
          </AuthOnlyRoute>
        }
      />

      {/* App routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Inbox />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="leads" element={<Leads />} />
        <Route path="engagement" element={<Engagement />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="posts" element={<Posts />} />
        <Route path="reports" element={<Reports />} />
        <Route path="calendar" element={<Bookings />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="audit" element={<Audit />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/audit" element={<Audit />} />
      </Route>

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Admin />} />
        <Route path="posts" element={<AdminPosts />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <LocaleProvider>
            <NotifyProvider>
              <AppRoutes />
              <Toaster />
            </NotifyProvider>
          </LocaleProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
